/* eslint-disable jsx-a11y/img-redundant-alt */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { Combobox } from "@/components/ui/combobox";
import { Pagination } from "@/components/ui/pagination";
import { inputClass } from "@/lib/formStyles";
import { UserRequest } from "api/types";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery } from "react-query";
import { toast } from "sonner";
import { deleteUser, getStaffPaginated } from "services/users";
import {
  ROLE_LABELS,
  STAFF_ROLES,
  getAdditionalRoles,
  getPrincipalRole,
  isAdmin,
  isDoctor,
} from "../../constants/roles";
import { createUser, updateUser } from "../../redux/users/actions";
import DashboardWrapper from "../Layout/DashboardWrapper";
import AdditionalRolesField from "../Others/AdditionalRolesField";
import Alert from "../Others/Alert";
import DeleteDoctorDialog from "../Others/DeleteDoctorDialog";
import RoleBadge from "../Others/RoleBadge";
import ModalUI from "../legacy/Modal";
import ShowUser from "./ShowUser";
import { getUserData } from "../../utils/storageUtils";

// Rôles assignables depuis cet écran. Le back n'accepte que ces trois-là
// pour un compte du personnel (`ROLE_PATIENT` / `ROLE_INSURER` ont leurs
// propres écrans et leurs propres endpoints de création).
const STAFF_ROLE_OPTIONS = STAFF_ROLES.map((role: string) => ({
  label: ROLE_LABELS[role as keyof typeof ROLE_LABELS],
  value: role,
}));

const initialsOf = (firstName?: string, lastName?: string) =>
  ((firstName?.[0] || "") + (lastName?.[0] || "")).toUpperCase() || null;

type UserForm = Partial<UserRequest> & { id?: number | string };

// Variables de la mutation de suppression. `replacementDoctorId` n'est
// renseigné que pour un médecin (transfert de ses patients) ; le message de
// succès est décidé par l'appelant, qui seul sait quel rôle il supprime.
type DeleteUserVariables = {
  id: number | string;
  replacementDoctorId?: number;
  successMessage: string;
};

const Users = () => {
  const dispatch = useDispatch();
  const [form, setForm] = useState<UserForm>({});
  const [editingId, setEditingId] = useState<number | string | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize] = useState(20);

  // Debounce de la recherche pour éviter une rafale d'appels back.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Flux Redux/saga : on s'abonne à l'état des mutations create/update
  // pour décider de fermer la modale (succès) ou non (échec 4xx). Les
  // sagas `createUser` / `updateUser` (redux/users/saga.js) toast déjà
  // via Sonner sur les deux issues, et alimentent `errors` avec le
  // payload `adaptResponseError(result)` en cas d'échec.
  const creatingUser = useSelector(
    (state: any) => state.usersReducer?.creating_user || false
  );
  const updatingUser = useSelector(
    (state: any) => state.usersReducer?.updating_user || false
  );
  const usersErrors = useSelector(
    (state: any) => state.usersReducer?.errors || []
  );
  const isEditing = editingId != null;
  const mutatingUser = isEditing ? updatingUser : creatingUser;

  // Seul un administrateur peut changer le rôle d'un compte existant :
  // le back répond 403 sinon (règle miroir S3). On lit `userdata` au
  // rendu — jamais au chargement du module — pour rester correct après
  // un changement de session.
  const canChangeRole = isAdmin(getUserData());
  const isRoleSelectorDisabled = isEditing && !canChangeRole;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((form) => ({
      ...form,
      [e.target.name]: e.target.value,
    }));

  /**
   * Payload du `PUT /users/{id}`. `roleName` et `additionalRoles` n'y
   * figurent que si l'utilisateur connecté est administrateur : envoyer
   * l'un de ces champs depuis un profil qui n'a pas le droit de les
   * modifier ferait échouer (403) une simple correction de numéro de
   * téléphone.
   */
  const buildUpdatePayload = (): UserForm => {
    const { roleName, additionalRoles, ...withoutRoles } = form;
    return canChangeRole
      ? { ...form, id: editingId ?? undefined }
      : { ...withoutRoles, id: editingId ?? undefined };
  };

  const handleSubmit = (e: React.MouseEvent) => {
    e.preventDefault();
    // Ne pas fermer la modale ici : c'est l'effet de transition
    // `creating_user|updating_user true → false` qui décide selon
    // `usersErrors`. Fermer en avance perdrait la saisie sur 409
    // (email/username déjà pris) et masquerait l'erreur derrière
    // l'animation de fermeture.
    if (isEditing) {
      dispatch(updateUser(buildUpdatePayload()));
    } else {
      dispatch(createUser(form));
    }
  };

  // Phase 2.H — Listing du personnel interne (staff = tout sauf PATIENT et
  // INSURER) servi par GET /users/staff (paginé + recherche serveur).
  const {
    isLoading,
    data: resultData,
    isFetching,
    refetch,
  } = useQuery(
    ["staff", debouncedSearch, page],
    () => getStaffPaginated(debouncedSearch, page, pageSize),
    {
      staleTime: 30000,
      keepPreviousData: true,
    }
  );

  // Suppression : useMutation + modale de confirmation. Comme dans
  // Patient/index.tsx, `apiRequest.notifyApiError` toast déjà tout `!ok`
  // côté wrapper — on ne duplique pas `toast.error` ici.
  const [deleteCandidate, setDeleteCandidate] = useState<UserRequest | null>(
    null
  );
  const { mutate: mutateDelete, isLoading: isDeleting } = useMutation(
    (variables: DeleteUserVariables) =>
      deleteUser(variables.id, {
        replacementDoctorId: variables.replacementDoctorId,
      }),
    {
      onSuccess: (result: any, variables: DeleteUserVariables) => {
        if (!result?.ok) return;
        toast.success(variables.successMessage);
        setDeleteCandidate(null);
        refetch();
      },
    }
  );

  // Supprimer un médecin exige de désigner le confrère qui reprend ses
  // patients et ses entrées de file d'attente : on ouvre alors la modale
  // dédiée, partagée avec l'écran « Personnel médecin ». Les autres rôles
  // gardent la confirmation générique.
  const isDoctorDeletion = isDoctor(deleteCandidate);

  const confirmDeleteDoctor = (
    replacementDoctorId: number,
    replacementDoctorName: string
  ) => {
    const doctorId = (deleteCandidate as any)?.id;
    if (doctorId == null) return;
    mutateDelete({
      id: doctorId,
      replacementDoctorId,
      successMessage: `Médecin supprimé, patients transférés à ${replacementDoctorName}.`,
    });
  };

  const confirmDeleteUser = () => {
    const userId = (deleteCandidate as any)?.id;
    if (userId == null) return;
    mutateDelete({ id: userId, successMessage: "Utilisateur supprimé" });
  };

  const paginatedData: any = resultData?.data || {};
  const data: UserRequest[] = paginatedData.content || [];
  const totalPages = paginatedData.totalPages || 0;
  const totalElements = paginatedData.totalElements || 0;
  const currentPage = paginatedData.currentPage || 0;
  const hasNext = paginatedData.hasNext || false;
  const hasPrevious = paginatedData.hasPrevious || false;

  const [isOpen, setIsOpen] = useState(false);
  const [localUser, setLocalUser] = useState<UserRequest>();
  const [openFormModal, setOpenFormModal] = useState(false);

  // Détecte la fin de `dispatch(createUser|updateUser(...))` en
  // surveillant les flags `creating_user` / `updating_user` du store :
  // true → false signifie que le saga a fini (succès OU échec). On lit
  // `usersErrors` à ce moment-là :
  // - vide → succès → fermer la modale + reset form + refetch liste ;
  // - non vide → échec → laisser la modale ouverte (le toast Sonner
  //   du saga affiche déjà le message back côté utilisateur).
  //
  // Phase 2.H : le saga ne dispatch plus `GET_USERS_REQUEST` (listing
  // massif interdit) — c'est à l'écran consommateur de rafraîchir sa
  // query paginée. On le fait ici via `refetch`.
  const wasMutating = useRef(false);
  useEffect(() => {
    if (wasMutating.current && !mutatingUser) {
      const succeeded = !usersErrors || usersErrors.length === 0;
      if (succeeded) {
        setOpenFormModal(false);
        setForm({});
        setEditingId(null);
        refetch();
      }
    }
    wasMutating.current = mutatingUser;
  }, [mutatingUser, usersErrors, refetch]);

  const openCreateModal = () => {
    setEditingId(null);
    setForm({});
    setOpenFormModal(true);
  };

  const openEditModal = (user: any) => {
    setEditingId(user?.id ?? null);
    // Pré-remplit avec le shape attendu par UPDATE /users/{id}. Le back
    // renvoyant un UserResponse polymorphe, le champ `role` peut tenir
    // lieu de `roleName` pour la requête PUT (cf. UserRequest.roleName).
    setForm({
      id: user?.id,
      firstName: user?.firstName ?? "",
      lastName: user?.lastName ?? "",
      email: user?.email ?? "",
      userName: user?.userName ?? "",
      phoneNumber: user?.phoneNumber ?? "",
      address: user?.address ?? "",
      roleName: (user?.roleName ?? user?.role) as any,
      additionalRoles: getAdditionalRoles(user) as any,
    });
    setOpenFormModal(true);
  };

  const closeFormModal = () => {
    setOpenFormModal(false);
    setEditingId(null);
    setForm({});
  };

  return (
    <DashboardWrapper showSpinner={isLoading || isFetching}>
      <>
      <ShowUser
        isOpen={isOpen}
        data={localUser}
        onClose={() => setIsOpen(false)}
      />
      <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both">
        {/* Hero */}
        <header className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold">
                Annuaire interne
              </p>
              <h1 className="mt-1 text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
                Utilisateurs
              </h1>
              <p className="mt-1 text-sm text-slate-500">
                {totalElements} compte{totalElements > 1 ? "s" : ""} actif
                {totalElements > 1 ? "s" : ""} (médecins, secrétaires, admins)
              </p>
            </div>
            <button
              type="button"
              onClick={openCreateModal}
              className="group/btn relative inline-flex items-center gap-2 overflow-hidden self-start sm:self-end rounded-xl bg-green-600 hover:bg-green-700 active:bg-green-800 text-white text-sm font-medium px-5 py-3 shadow-lg shadow-green-600/25 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-emerald-500/30"
            >
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 -left-full w-1/3 -skew-x-12 bg-gradient-to-r from-transparent via-white/25 to-transparent transition-all duration-700 group-hover/btn:left-full"
              />
              <i className="bx bx-user-plus text-lg" />
              Ajouter un utilisateur
            </button>
          </div>
        </header>

        {/* Search bar */}
        <div className="animate-in fade-in slide-in-from-bottom-2 duration-500 delay-100 fill-mode-both">
          <label className="relative block w-full md:w-96">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-lg pointer-events-none">
              <i className="bx bx-search" />
            </span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={inputClass + " pl-11 mt-0"}
              placeholder="Rechercher un utilisateur…"
            />
          </label>
        </div>

        {/* Errors */}
        {usersErrors && usersErrors.length > 0 && (
          <div className="space-y-2">
            {usersErrors.map((error: any, idx: number) => (
              <Alert key={idx} message={error.message} />
            ))}
          </div>
        )}

        {/* Table */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-500 delay-200 fill-mode-both">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50/70 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                  <th className="px-4 py-3 text-left">Utilisateur</th>
                  <th className="px-4 py-3 text-left">E-mail</th>
                  <th className="px-4 py-3 text-left">Rôle</th>
                  <th className="px-4 py-3 text-left">Statut</th>
                  <th className="px-4 py-3 text-right" />
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-16 text-center">
                      <i className="bx bx-group text-4xl text-slate-300" />
                      <p className="mt-2 text-sm font-medium text-slate-600">
                        Aucun utilisateur
                      </p>
                      <p className="mt-1 text-xs text-slate-400">
                        Ajustez votre recherche ou ajoutez un nouveau compte.
                      </p>
                    </td>
                  </tr>
                ) : (
                  data.map((user: any, idx) => {
                    const initials = initialsOf(user?.firstName, user?.lastName);
                    return (
                      <tr
                        key={user?.id ?? idx}
                        className="group/row border-t border-slate-100 hover:bg-emerald-50/40 transition-colors"
                      >
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3 min-w-0">
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-green-600 text-sm font-semibold text-white ring-2 ring-white shadow-sm">
                              {initials || <i className="bx bx-user" />}
                            </span>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-slate-800 truncate">
                                {user?.firstName} {user?.lastName}
                              </p>
                              {user?.userName && (
                                <p className="text-xs text-slate-400 truncate">
                                  @{user.userName}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-slate-600 whitespace-nowrap">
                          {user?.email || (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <RoleBadge role={getPrincipalRole(user)} />
                            {getAdditionalRoles(user).map((role: string) => (
                              <RoleBadge
                                key={role}
                                role={role}
                                variant="additional"
                              />
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-100 bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                            Actif
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right">
                          <div className="inline-flex items-center gap-1 opacity-70 group-hover/row:opacity-100 transition-opacity">
                            <button
                              type="button"
                              onClick={() => {
                                setLocalUser(user);
                                setIsOpen(true);
                              }}
                              className="inline-flex items-center justify-center h-9 w-9 rounded-full text-slate-400 hover:bg-emerald-50 hover:text-emerald-700 transition"
                              aria-label="Voir le profil"
                              title="Voir le profil"
                            >
                              <i className="bx bx-show text-lg" />
                            </button>
                            <button
                              type="button"
                              onClick={() => openEditModal(user)}
                              className="inline-flex items-center justify-center h-9 w-9 rounded-full text-slate-400 hover:bg-amber-50 hover:text-amber-600 transition"
                              aria-label="Modifier"
                              title="Modifier"
                            >
                              <i className="bx bx-edit-alt text-lg" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeleteCandidate(user)}
                              className="inline-flex items-center justify-center h-9 w-9 rounded-full text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition"
                              aria-label="Supprimer"
                              title="Supprimer"
                            >
                              <i className="bx bx-trash text-lg" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="animate-in fade-in slide-in-from-bottom-2 duration-500 delay-300 fill-mode-both">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalElements={totalElements}
            currentCount={data.length}
            hasNext={hasNext}
            hasPrevious={hasPrevious}
            onPageChange={setPage}
            itemLabel="utilisateur"
            isLoading={isLoading}
          />
        </div>

        {/* Modal — création / édition */}
        <ModalUI
          isOpen={openFormModal}
          onClose={closeFormModal}
          styles={{ height: "80%" }}
        >
          <form className="h-full flex flex-col">
            <div className="px-6 sm:px-8 pt-6 pb-4 border-b border-slate-100">
              <p className="text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold">
                {isEditing ? "Édition" : "Nouveau compte"}
              </p>
              <h2 className="mt-1 text-lg font-semibold text-slate-800">
                {isEditing
                  ? "Modifier l'utilisateur"
                  : "Ajouter un utilisateur"}
              </h2>
            </div>
            <div className="px-6 sm:px-8 py-6 flex-1 overflow-auto space-y-5">
              {usersErrors && usersErrors.length > 0 && (
                <div className="space-y-2">
                  {usersErrors.map((error: any, idx: number) => (
                    <Alert key={idx} message={error.message} />
                  ))}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="block text-sm font-medium text-slate-700">
                    Prénom
                  </span>
                  <input
                    name="firstName"
                    value={form.firstName ?? ""}
                    onChange={handleChange}
                    type="text"
                    placeholder="Prénom"
                    className={inputClass}
                  />
                </label>
                <label className="block">
                  <span className="block text-sm font-medium text-slate-700">
                    Nom
                  </span>
                  <input
                    type="text"
                    name="lastName"
                    value={form.lastName ?? ""}
                    onChange={handleChange}
                    placeholder="Nom"
                    className={inputClass}
                  />
                </label>
              </div>
              <label className="block">
                <span className="block text-sm font-medium text-slate-700">
                  Email
                </span>
                <div className="relative">
                  <input
                    type="email"
                    name="email"
                    value={form.email ?? ""}
                    onChange={handleChange}
                    placeholder="exemple@compassion.bj"
                    className={inputClass + " pr-10"}
                  />
                  <i className="bx bx-envelope absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg pointer-events-none" />
                </div>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="block text-sm font-medium text-slate-700">
                    Nom d'utilisateur
                  </span>
                  <input
                    type="text"
                    name="userName"
                    value={form.userName ?? ""}
                    onChange={handleChange}
                    placeholder="@identifiant"
                    className={inputClass}
                  />
                </label>
                <label className="block">
                  <span className="block text-sm font-medium text-slate-700">
                    Téléphone
                  </span>
                  <input
                    type="text"
                    name="phoneNumber"
                    value={form.phoneNumber ?? ""}
                    onChange={handleChange}
                    placeholder="(+229) …"
                    className={inputClass}
                  />
                </label>
              </div>
              {!isEditing && (
                <label className="block">
                  <span className="block text-sm font-medium text-slate-700">
                    Mot de passe
                  </span>
                  <input
                    type="password"
                    name="password"
                    value={form.password ?? ""}
                    onChange={handleChange}
                    placeholder="Mot de passe initial"
                    className={inputClass}
                  />
                </label>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="block text-sm font-medium text-slate-700">
                    Adresse
                  </span>
                  <input
                    type="text"
                    name="address"
                    value={form.address ?? ""}
                    onChange={handleChange}
                    placeholder="Ville, quartier"
                    className={inputClass}
                  />
                </label>
                <label className="block">
                  <span className="block text-sm font-medium text-slate-700">
                    Rôle
                  </span>
                  {/* popoverClassName="z-[130]" : le Radix Popover sort par
                      défaut à z-50, alors que ModalUI vit à z-[120]. Sans
                      override, la liste tombe derrière la modale et le
                      select paraît "vide". */}
                  <Combobox
                    options={STAFF_ROLE_OPTIONS}
                    value={(form as any)?.roleName ?? null}
                    disabled={isRoleSelectorDisabled}
                    placeholder="Sélectionner un rôle"
                    searchPlaceholder="Rechercher un rôle…"
                    className="w-full mt-1"
                    popoverClassName="z-[130]"
                    onChange={(val) => {
                      const nextPrincipalRole = val?.value as any;
                      // Le rôle principal ne peut pas figurer aussi dans
                      // les rôles additionnels (le back le refuse) : on
                      // le décoche dès qu'il devient le principal.
                      setForm((form) => ({
                        ...form,
                        roleName: nextPrincipalRole,
                        additionalRoles: (form.additionalRoles ?? []).filter(
                          (role) => role !== nextPrincipalRole
                        ),
                      }));
                    }}
                  />
                  {isRoleSelectorDisabled && (
                    <span className="mt-1 block text-xs text-slate-400">
                      Seul un administrateur peut modifier le rôle d'un
                      compte.
                    </span>
                  )}
                </label>
              </div>
              {/* Rôles additionnels : réservés à l'administrateur, comme
                  le rôle principal (le back répond 403 sinon). */}
              {canChangeRole && (
                <AdditionalRolesField
                  principalRole={(form as any)?.roleName ?? null}
                  // Double cast : `RoleNameEnum` est déclaré en enum
                  // numérique dans `api/types.ts` (valeurs `<any>` castées),
                  // TypeScript ne le rapproche donc pas de `string`.
                  value={((form.additionalRoles ?? []) as unknown) as string[]}
                  onChange={(additionalRoles) =>
                    setForm((form) => ({
                      ...form,
                      additionalRoles: additionalRoles as any,
                    }))
                  }
                />
              )}
            </div>
            <div className="px-6 sm:px-8 py-4 border-t border-slate-100 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeFormModal}
                className="inline-flex items-center rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 transition"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={mutatingUser}
                className="inline-flex items-center gap-2 rounded-xl bg-green-600 hover:bg-green-700 active:bg-green-800 text-white text-sm font-medium px-5 py-2.5 shadow-lg shadow-green-600/25 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-emerald-500/30 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-green-600"
              >
                {mutatingUser ? (
                  <>
                    <i className="bx bx-loader-alt bx-spin text-lg" />
                    En cours…
                  </>
                ) : (
                  <>
                    <i className="bx bx-check text-lg" />
                    {isEditing ? "Mettre à jour" : "Enregistrer"}
                  </>
                )}
              </button>
            </div>
          </form>
        </ModalUI>

        {/* Suppression d'un médecin : transfert obligatoire des patients. */}
        <DeleteDoctorDialog
          doctor={deleteCandidate}
          open={deleteCandidate != null && isDoctorDeletion}
          onConfirm={confirmDeleteDoctor}
          onCancel={() => setDeleteCandidate(null)}
          isLoading={isDeleting}
        />

        {/* Modal — confirmation suppression (rôles hors médecin) */}
        <ModalUI
          isOpen={deleteCandidate != null && !isDoctorDeletion}
          onClose={() => {
            if (!isDeleting) setDeleteCandidate(null);
          }}
          styles={{ width: "min(440px, 92vw)", padding: 0 }}
        >
          <div className="flex flex-col">
            <div className="flex items-start gap-4 px-6 pt-6">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-rose-50 text-rose-600 ring-1 ring-rose-100">
                <i className="bx bx-trash text-2xl" />
              </div>
              <div className="space-y-1">
                <p className="text-[11px] uppercase tracking-[0.32em] text-rose-600 font-semibold">
                  Action irréversible
                </p>
                <h2 className="text-lg font-semibold text-slate-900 leading-tight">
                  Supprimer cet utilisateur ?
                </h2>
                <p className="text-sm text-slate-500">
                  Le compte de{" "}
                  <span className="font-medium text-slate-800">
                    {deleteCandidate?.firstName} {deleteCandidate?.lastName}
                  </span>{" "}
                  sera supprimé définitivement. Cette action ne peut pas être
                  annulée.
                </p>
              </div>
            </div>
            <div className="mt-6 flex items-center gap-3 border-t border-slate-100 bg-slate-50/50 px-6 py-4">
              <button
                type="button"
                onClick={() => setDeleteCandidate(null)}
                disabled={isDeleting}
                className="flex-1 inline-flex items-center justify-center h-11 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-800 transition-colors disabled:opacity-60"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={confirmDeleteUser}
                disabled={isDeleting || (deleteCandidate as any)?.id == null}
                className="flex-[1.4] inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-rose-600 hover:bg-rose-700 active:bg-rose-800 text-white text-sm font-medium shadow-lg shadow-rose-600/25 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isDeleting ? (
                  <>
                    <i className="bx bx-loader-alt animate-spin text-base" />
                    <span>Suppression…</span>
                  </>
                ) : (
                  <>
                    <i className="bx bx-trash text-base" />
                    <span>Supprimer</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </ModalUI>
      </div>
      </>
    </DashboardWrapper>
  );
};

export default Users;
